import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as OrderedListIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';
import { stitchColors, stitchFonts } from '../theme/tokens';

export interface RichTextEditorProps {
  content: any;
  onChange?: (json: any, plainText?: string) => void;
  editable?: boolean;
  preserveLayout?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  editable = true,
  preserveLayout = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: preserveLayout ? [1, 2, 3, 4, 5, 6] : [1, 2, 3] },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: content || { type: 'doc', content: [] },
    editable,
    editorProps: {
      attributes: { 'aria-label': editable ? 'Document content' : 'Document preview', role: 'textbox' },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getJSON(), ed.getText());
    },
  });

  useEffect(() => {
    if (editor && content) {
      const currentJSON = JSON.stringify(editor.getJSON());
      const nextJSON = JSON.stringify(content);
      if (currentJSON !== nextJSON) {
        editor.commands.setContent(content, { emitUpdate: !preserveLayout });
      }
    }
  }, [content, editor, preserveLayout]);

  useEffect(() => {
    editor?.setEditable(editable, false);
  }, [editable, editor]);

  if (!editor) {
    return null;
  }

  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 2, withHeaderRow: true }).run();
  };

  return (
    <Box
      sx={{
        border: editable ? `1px solid ${stitchColors.outlineVariant}` : 'none',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#ffffff',
      }}
    >
      {editable && (
        <Paper
          elevation={0}
          sx={{
            p: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            borderBottom: `1px solid ${stitchColors.outlineVariant}`,
            bgcolor: stitchColors.surfaceContainerLowest,
          }}
        >
          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="Heading 1">
              <Button
                variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                sx={{ minWidth: 36, px: 1, fontWeight: 700 }}
              >
                H1
              </Button>
            </Tooltip>
            <Tooltip title="Heading 2">
              <Button
                variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                sx={{ minWidth: 36, px: 1, fontWeight: 700 }}
              >
                H2
              </Button>
            </Tooltip>
            <Tooltip title="Heading 3">
              <Button
                variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                sx={{ minWidth: 36, px: 1, fontWeight: 700 }}
              >
                H3
              </Button>
            </Tooltip>
            <Tooltip title="Normal Paragraph">
              <Button
                variant={editor.isActive('paragraph') ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().setParagraph().run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                P
              </Button>
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="Bold">
              <Button
                variant={editor.isActive('bold') ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleBold().run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <BoldIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Italic">
              <Button
                variant={editor.isActive('italic') ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <ItalicIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Underline">
              <Button
                variant={editor.isActive('underline') ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <UnderlineIcon fontSize="small" />
              </Button>
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="Bullet List">
              <Button
                variant={editor.isActive('bulletList') ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <BulletListIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Numbered List">
              <Button
                variant={editor.isActive('orderedList') ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <OrderedListIcon fontSize="small" />
              </Button>
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <ButtonGroup size="small" variant="outlined">
            <Tooltip title="Align Left">
              <Button
                variant={editor.isActive({ textAlign: 'left' }) ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <AlignLeftIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Align Center">
              <Button
                variant={editor.isActive({ textAlign: 'center' }) ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <AlignCenterIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Align Right">
              <Button
                variant={editor.isActive({ textAlign: 'right' }) ? 'contained' : 'outlined'}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                sx={{ minWidth: 36, px: 1 }}
              >
                <AlignRightIcon fontSize="small" />
              </Button>
            </Tooltip>
          </ButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Tooltip title="Insert Table">
            <Button
              size="small"
              variant="outlined"
              onClick={addTable}
              startIcon={<TableIcon fontSize="small" />}
              sx={{ px: 1 }}
            >
              Table
            </Button>
          </Tooltip>
        </Paper>
      )}

      <Box
        sx={{
          p: editable ? { xs: 2, md: 3 } : 0,
          minHeight: editable ? 350 : 0,
          fontFamily: stitchFonts.headline,
          color: stitchColors.onBackground,
          lineHeight: 1.8,
          '& .ProseMirror': {
            outline: 'none',
            minHeight: editable ? 350 : 0,
            '& p': { my: 1 },
            '& h1': { fontSize: '1.75rem', fontWeight: 700, mt: 2, mb: 1 },
            '& h2': { fontSize: '1.35rem', fontWeight: 600, mt: 2, mb: 1 },
            '& h3': { fontSize: '1.15rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
            '& ul, & ol': { pl: 3, my: 1 },
            '& table': {
              ...(preserveLayout ? {} : { width: '100%' }),
              borderCollapse: 'collapse',
              my: 2,
              '& th, & td': {
                border: `1px solid ${stitchColors.outlineVariant}`,
                p: 1,
                minWidth: preserveLayout ? undefined : 80,
                textAlign: 'left',
              },
              '& th': {
                bgcolor: stitchColors.surfaceContainerHigh,
                fontWeight: 600,
              },
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
